import * as React from "react";
import {
  ChamberReportFields,
  fieldGroupFirstFieldToGroupName,
  IChamberTrial,
  sliderRanges,
} from "./columns";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import BalanceIcon from "@mui/icons-material/Balance";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import CachedIcon from "@mui/icons-material/Cached";
import {
  AnalysisSliderSectionColor,
  getGoodnessDensity,
} from "../../Common/GasAnalysisReport/AnalysisConstants";
import { showSnackbar } from "../../../../service/snackbar.service";
import ProgressButton from "../../Common/ProgressButton";
import ReportValuesMiniChart from "./ReportValuesMiniChart";
import ReportValueMiniSlider from "./ReportValueMiniSlider";
import { useAppContext } from "../../../../contexts/useStorageContext";

const getGoodnessColor = (
  value: number,
  fieldName: string,
  conversionFactor: number,
  valueRanges: { [key: string]: { min: number; max: number } }
) => {
  let fromValue = 0;
  let toValue = 0;
  let convertedValue = 0;
  if (Number.isNaN(value))
    return { color: "", difference: 0, differencePercentage: 0 };
  switch (fieldName) {
    case ChamberReportFields.waterL:
      convertedValue = value * conversionFactor;
      fromValue = valueRanges.drinkingPerDay.min;
      toValue = valueRanges.drinkingPerDay.max;
      if (convertedValue < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: convertedValue - fromValue,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (convertedValue > toValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: convertedValue - toValue,
          differencePercentage:
            ((convertedValue - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            convertedValue,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    case ChamberReportFields.eatingMin:
      convertedValue = value * conversionFactor;
      fromValue = valueRanges.eatingMinsPerDay.min;
      toValue = valueRanges.eatingMinsPerDay.max;
      if (convertedValue < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: convertedValue - fromValue,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (convertedValue > toValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: convertedValue - toValue,
          differencePercentage:
            ((convertedValue - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            convertedValue,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    case ChamberReportFields.standingRestingMin:
      convertedValue = value * conversionFactor;
      fromValue = valueRanges.restingMinsPerDay.min;
      toValue = valueRanges.restingMinsPerDay.max;
      if (convertedValue < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: convertedValue - fromValue,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (convertedValue > toValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: convertedValue - toValue,
          differencePercentage:
            ((convertedValue - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            convertedValue,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    case ChamberReportFields.c02G:
      convertedValue = value * conversionFactor;
      fromValue = valueRanges.Co2gPerDay.min;
      toValue = valueRanges.Co2gPerDay.max;
      if (convertedValue < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: convertedValue - fromValue,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (convertedValue > toValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: convertedValue - toValue,
          differencePercentage:
            ((convertedValue - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            convertedValue,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    case ChamberReportFields.ch4G:
      convertedValue = value * conversionFactor;
      fromValue = valueRanges.Ch4gPerDay.min;
      toValue = valueRanges.Ch4gPerDay.max;
      if (convertedValue < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: convertedValue - fromValue,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (convertedValue > toValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: convertedValue - toValue,
          differencePercentage:
            ((convertedValue - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            convertedValue,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((convertedValue - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    case ChamberReportFields.animalWeightKg:
      fromValue = valueRanges.weight.min;
      toValue = valueRanges.weight.max;
      if (value < fromValue) {
        return {
          color: AnalysisSliderSectionColor.Red,
          difference: value - fromValue,
          differencePercentage:
            ((value - fromValue) / (toValue - fromValue)) * 100,
        };
      } else if (value > toValue) {
        return {
          color: AnalysisSliderSectionColor.Yellow,
          difference: value - toValue,
          differencePercentage:
            ((value - toValue) / (toValue - fromValue)) * 100,
        };
      } else {
        return {
          color: `${AnalysisSliderSectionColor.Green}${getGoodnessDensity(
            value,
            fromValue,
            toValue
          )}`,
          differencePercentage:
            ((value - fromValue) / (toValue - fromValue)) * 100,
        };
      }

    default:
      return { color: "", difference: 0, differencePercentage: 0 };
  }
};

enum ConditionEnum {
  NoHeatStress = "No Heat Stress",
  ModerateHeatStress = "Moderate Heat Stress",
  SevereHeatStress = "Severe Heat Stress",
  DeadCows = "Dead Cows",
}

const heatStressConditionColors = {
  [ConditionEnum.NoHeatStress]: "#00ffff",
  [ConditionEnum.ModerateHeatStress]: "#00ff01",
  [ConditionEnum.SevereHeatStress]: "#ffcc00",
  [ConditionEnum.DeadCows]: "#fd0001",
};

function getHeatStressCondition(
  temperature: number,
  humidity: number
): ConditionEnum {
  const THI = temperature - (0.55 - 0.0055 * humidity) * (temperature - 14.5);

  if (THI <= 72) {
    return ConditionEnum.NoHeatStress;
  } else if (THI <= 79) {
    return ConditionEnum.ModerateHeatStress;
  } else if (THI <= 89) {
    return ConditionEnum.SevereHeatStress;
  } else {
    return ConditionEnum.DeadCows;
  }
}

export default function ChamberReport({
  title,
  trials,
  onClose,
  byDate,
  byCow,
}: {
  title?: string;
  trials: IChamberTrial[];
  onClose: () => void;
  byDate?: boolean;
  byCow?: boolean;
}) {
  const { state } = useAppContext();
  const { chamberReportValueRanges } = state;
  const [comparisonFirst, setComparisonFirst] = React.useState<IChamberTrial>(
    trials[1]
  );
  const [comparisonSecond, setComparisonSecond] = React.useState<IChamberTrial>(
    trials[0]
  );
  const [printMode, setPrintMode] = React.useState(false);
  const [downloadingReport, setDownloadingReport] = React.useState(false);
  const printRef = React.useRef<HTMLElement>();

  const handleGeneratePdf = async () => {
    try {
      const element = printRef.current;
      if (!element) return;
      setDownloadingReport(true);
      const canvas = await html2canvas(element, { scale: 1 });
      const data = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "in",
        format: "letter",
      });
      const imgProperties = pdf.getImageProperties(data);
      const pdfWidth = 8.5; // Width of PDF in inches
      const pdfHeight = 11; // Height of PDF in inches
      const imgWidth = imgProperties.width;
      const imgHeight = imgProperties.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const newWidth = imgWidth * ratio;
      const newHeight = imgHeight * ratio;
      const xOffset = (pdfWidth - newWidth) / 2;
      const yOffset = (pdfHeight - newHeight) / 2;
      pdf.addImage(data, "PNG", xOffset, yOffset, newWidth, newHeight);
      pdf.save(
        `chamber_report-${
          byCow ? `cow-${trials[0]?.fields?.cow}` : trials[0]?.fields?.date
        }-${
          byCow
            ? `${`(${trials[0]?.fields?.date})`}~${`(${
                trials[trials.length - 1]?.fields?.date
              })`}`
            : `${trials.map((trial) => trial.chamberName).join("-")}`
        }.pdf`
      );
      setDownloadingReport(false);
    } catch (error) {
      showSnackbar("danger", "", "Could not generate the PDF.");
    } finally {
      setDownloadingReport(false);
      setPrintMode(false);
    }
  };

  return (
    <Dialog open onClose={onClose} scroll="paper" maxWidth={false}>
      <DialogTitle>
        <div style={{ display: "flex", alignItems: "center" }}>
          Chamber Report
          <div style={{ flex: 1 }} />
          <ProgressButton
            inProgress={downloadingReport}
            startIcon={<FileDownloadIcon />}
            variant="outlined"
            onClick={() => {
              setPrintMode(true);
              setTimeout(() => {
                handleGeneratePdf();
              }, 300);
            }}
            title="Download PDF"
          />
        </div>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            "& td": {
              padding: "4px 12px",
            },
            "& th": {
              padding: "4px 12px",
            },
            minWidth: printMode ? "1000px" : undefined,
            padding: printMode ? "24px" : undefined,
          }}
          ref={printRef}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              marginBottom: "12px",
            }}
          >
            <img
              src="fermentrics-logo.png"
              style={{
                maxHeight: "45px",
                width: "auto",
              }}
            />
            <div style={{ flex: 1 }} />
          </div>
          <TableContainer
            sx={{
              border: "solid 1px #ddd",
              borderRadius: "4px",
              overflowX: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Box sx={{ color: "primary.main" }}>{title}</Box>
                  </TableCell>
                  {trials.map((trial, trialIndex) => (
                    <TableCell key={trialIndex}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        {trial.chamberName}
                        {byCow ? (
                          <Box sx={{ color: "warning.main" }}>
                            {trial.fields?.date}
                          </Box>
                        ) : (
                          <Box sx={{ color: "warning.main" }}>
                            {trial.fields?.cow}
                          </Box>
                        )}
                      </div>
                    </TableCell>
                  ))}
                  {trials?.length == 2 ? (
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <BalanceIcon sx={{ fontSize: "16px", mr: 1 }} />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <Box sx={{ color: "warning.main", mr: "4px" }}>
                            {byCow
                              ? comparisonFirst.date
                              : comparisonFirst.chamberName}
                          </Box>
                          to
                          <Box sx={{ color: "warning.main", ml: "4px" }}>
                            {byCow
                              ? comparisonSecond.date
                              : comparisonSecond.chamberName}
                          </Box>
                        </div>
                        {printMode ? null : (
                          <SwapHorizontalCircleIcon
                            color="primary"
                            sx={{ cursor: "pointer", ml: 1 }}
                            onClick={() => {
                              setComparisonFirst(comparisonSecond);
                              setComparisonSecond(comparisonFirst);
                            }}
                          />
                        )}
                      </div>
                    </TableCell>
                  ) : trials?.length > 2 ? (
                    <TableCell>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <TrendingUpIcon />
                      </div>
                    </TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.keys(ChamberReportFields).map((field, fieldIndex) => {
                  const fieldName =
                    ChamberReportFields[
                      field as keyof typeof ChamberReportFields
                    ];
                  const fieldGroupName = (
                    fieldGroupFirstFieldToGroupName as any
                  )[fieldName];
                  const comparisonFirstValue = (
                    comparisonFirst.fields as any
                  )?.[field];
                  const comparisonSecondValue = (
                    comparisonSecond.fields as any
                  )?.[field];
                  let comparisonDifferencePercentage = null;
                  if (
                    comparisonFirstValue &&
                    comparisonSecondValue &&
                    !Number.isNaN(Number(comparisonFirstValue)) &&
                    !Number.isNaN(Number(comparisonSecondValue)) &&
                    ![ChamberReportFields.cow].includes(fieldName)
                  ) {
                    const comparisonFirstValueNumber =
                      Number(comparisonFirstValue);
                    const comparisonSecondValueNumber = Number(
                      comparisonSecondValue
                    );
                    const comparisonDifference =
                      comparisonFirstValueNumber - comparisonSecondValueNumber;
                    comparisonDifferencePercentage = Number(
                      Number(
                        (comparisonDifference / comparisonSecondValueNumber) *
                          100
                      ).toFixed(1)
                    );
                  }

                  const isNumericField = trials.every(
                    (trial) =>
                      ![ChamberReportFields.cow].includes(fieldName) &&
                      !Number.isNaN(Number((trial.fields as any)?.[field]))
                  );

                  return (
                    <TableRow
                      key={fieldIndex}
                      sx={{
                        borderTop: fieldGroupName
                          ? "solid 30px #0000"
                          : undefined,
                      }}
                    >
                      <TableCell>
                        <Box
                          sx={{
                            position: "relative",
                          }}
                        >
                          {fieldName}
                          {fieldGroupName ? (
                            <Box
                              sx={{
                                position: "absolute",
                                top: "-33px",
                                left: "-20px",
                                pl: "18px",
                                py: "4px",
                                minWidth: "3000px",
                                color: "warning.main",
                                background: "#efefef",
                              }}
                            >
                              {fieldGroupName}
                            </Box>
                          ) : null}
                        </Box>
                      </TableCell>
                      {trials.map((trial, trialIndex) => {
                        const value = (trial.fields as any)?.[field];
                        const displayValue = Number.isNaN(Number(value))
                          ? value
                          : Number(Number(value)?.toFixed(2));

                        return (
                          <TableCell key={trialIndex}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                maxWidth: "250px",
                              }}
                            >
                              {displayValue}
                              <div style={{ flex: 1 }} />
                              {Object.keys(
                                chamberReportValueRanges || sliderRanges
                              ).includes(fieldName) ? (
                                <Box>
                                  <ReportValueMiniSlider
                                    fieldName={fieldName as any}
                                    value={displayValue}
                                    valueRanges={
                                      chamberReportValueRanges || sliderRanges
                                    }
                                  />
                                </Box>
                              ) : null}
                            </div>
                          </TableCell>
                        );
                      })}
                      {trials?.length == 2 ? (
                        <TableCell sx={{ position: "relative" }}>
                          <div
                            style={{
                              position: "absolute",
                              width:
                                comparisonDifferencePercentage == null
                                  ? "0px"
                                  : `${Math.min(
                                      Math.abs(comparisonDifferencePercentage),
                                      100
                                    )}%`,
                              height: "100%",
                              left: "0px",
                              background:
                                comparisonDifferencePercentage == null
                                  ? "#0000"
                                  : comparisonDifferencePercentage == 0
                                  ? "#0000"
                                  : comparisonDifferencePercentage > 0
                                  ? `${AnalysisSliderSectionColor.Green}55`
                                  : `${AnalysisSliderSectionColor.Yellow}55`,
                              top: "0px",
                            }}
                          ></div>
                          {comparisonDifferencePercentage != null
                            ? `${
                                comparisonDifferencePercentage > 0 ? "+" : ""
                              }${comparisonDifferencePercentage}%`
                            : null}
                        </TableCell>
                      ) : trials?.length > 2 ? (
                        <TableCell>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              position: "relative",
                              minWidth: "100px",
                              overflow: "hidden",
                              height: "20px",
                            }}
                          >
                            {isNumericField ? (
                              <div
                                style={{
                                  position: "absolute",
                                }}
                              >
                                <ReportValuesMiniChart
                                  values={trials.map((trial) =>
                                    Number((trial.fields as any)?.[field])
                                  )}
                                  id={field}
                                />
                              </div>
                            ) : null}
                          </div>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
                <TableRow>
                  <TableCell>Heat Stress</TableCell>
                  {trials.map((trial, trialIndex) => (
                    <TableCell key={trialIndex}>
                      {trial.fields?.ambientChamberTempC &&
                      trial.fields?.averageHumidity ? (
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <div
                            style={{
                              width: "12px",
                              height: "12px",
                              backgroundColor:
                                heatStressConditionColors[
                                  getHeatStressCondition(
                                    Number(trial.fields?.ambientChamberTempC),
                                    Number(trial.fields?.averageHumidity)
                                  )
                                ],

                              marginRight: "4px",
                            }}
                          />
                          {getHeatStressCondition(
                            Number(trial.fields?.ambientChamberTempC),
                            Number(trial.fields?.averageHumidity)
                          )}
                        </div>
                      ) : null}
                    </TableCell>
                  ))}
                  {trials?.length == 2 ? (
                    <TableCell></TableCell>
                  ) : trials?.length > 2 ? (
                    <TableCell></TableCell>
                  ) : null}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            sx={{
              fontSize: "13px",
              color: "text.secondary",
              textAlign: "center",
              maxWidth: "1100px",
              margin: "auto",
              mt: 2,
              lineHeight: "1.3",
            }}
          >
            All environmental chamber work is in accordance with the rules and
            regulations of the CCAC and are conducted under the OMAFRA Research
            License # 0191-01. No animals were hurt during the collection of
            data. Data and Results are confidential. ISO Certified.
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
